import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import connectorService from '../services/connectorService';
import connectoriprangesService from '../services/connectoriprangesService';
import connectorsettingsService from '../services/connectorsettingsService';
import connectortrxsummaryService from '../services/connectortrxsummaryService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import './ConnectorForm.css';

function ConnectorForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, hotelid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    hotelid: '',
    accountid: '',
    connectoruuid: '',
    connectorName: '',
    connectorType: '',
    environment: 'production',
    connectorStatus: 'inactive',
    connectorUsername: '',
    connectorHost: '',
    connectorPort: '',
    connectorPassword: '',
    connectorDBName: '',
    sftpfrequency: '',
    sftpfolder: '',
    sftpdeletefile: 'N',
    connectorDescription: '',
    version: '',
    debug: 'N',
    region: '',
    encryptpayload: 'N',
    restrictip: 'N',
    allowresyncmsg: 'Y'
  });

  // State for IP Ranges
  const [ipRanges, setIpRanges] = useState([]);
  const [ipRangesCurrentPage, setIpRangesCurrentPage] = useState(1);
  const ipRangesPerPage = 5;

  // State for Settings
  const [settings, setSettings] = useState([]);

  // State for Hotels and Accounts
  const [hotels, setHotels] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);

  // State for Transaction Summaries
  const [trxSummaries, setTrxSummaries] = useState([]);
  const [trxSummariesCurrentPage, setTrxSummariesCurrentPage] = useState(1);
  const trxSummariesPerPage = 5;

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const [activeTab, setActiveTab] = useState('general');

  // Set active tab from URL hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['general', 'configuration', 'ipranges', 'settings', 'transactions'].includes(hash)) {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    // Auto-populate hotelid if creating from hotel page
    if (hotelid && isCreateMode) {
      console.log('Setting hotelid from params:', hotelid);
      setFormData(prev => ({ ...prev, hotelid: parseInt(hotelid) }));
    }
  }, [hotelid, isCreateMode]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchConnector();
    }
    if (isCreateMode) {
      fetchHotelsAndAccounts();
    }
  }, [id, isEditMode, isViewMode, isCreateMode]);

  const fetchHotelsAndAccounts = async () => {
    try {
      const accountsResponse = await accountService.getAllAccounts({});
      setAccounts(accountsResponse.accounts || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchHotels = async (accountid) => {
    if (!accountid) {
      setHotels([]);
      return;
    }
    try {
      const hotelsResponse = await hotelService.getAllHotels({ accountid });
      setHotels(hotelsResponse.hotels || []);
    } catch (err) {
      console.error('Error fetching hotels:', err);
      setHotels([]);
    }
  };

  // Watch for accountid changes to reload hotels
  useEffect(() => {
    if (isCreateMode && formData.accountid) {
      fetchHotels(formData.accountid);
    }
  }, [formData.accountid, isCreateMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAccountDropdown && !event.target.closest('.form-group')) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountDropdown]);

  const fetchConnector = async () => {
    try {
      setLoading(true);
      const connectorData = await connectorService.getConnectorById(id, hotelid);

      // Set all connector fields
      setFormData({
        id: connectorData.id || '',
        hotelid: connectorData.hotelid || '',
        accountid: connectorData.accountid || '',
        connectoruuid: connectorData.connectoruuid || '',
        connectorName: connectorData.connectorName || '',
        connectorType: connectorData.connectorType || '',
        environment: connectorData.environment || 'production',
        connectorStatus: connectorData.connectorStatus || 'inactive',
        connectorUsername: connectorData.connectorUsername || '',
        connectorHost: connectorData.connectorHost || '',
        connectorPort: connectorData.connectorPort || '',
        connectorPassword: connectorData.connectorPassword || '',
        connectorDBName: connectorData.connectorDBName || '',
        sftpfrequency: connectorData.sftpfrequency || '',
        sftpfolder: connectorData.sftpfolder || '',
        sftpdeletefile: connectorData.sftpdeletefile || 'N',
        connectorDescription: connectorData.connectorDescription || '',
        version: connectorData.version || '',
        debug: connectorData.debug || 'N',
        region: connectorData.region || '',
        encryptpayload: connectorData.encryptpayload || 'N',
        restrictip: connectorData.restrictip || 'N',
        allowresyncmsg: connectorData.allowresyncmsg || 'Y'
      });

      // Fetch IP ranges, settings, and transaction summaries in view/edit mode
      if (isViewMode || isEditMode) {
        await Promise.all([
          fetchIPRanges(),
          fetchSettings(),
          fetchTrxSummaries()
        ]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch connector');
    } finally {
      setLoading(false);
    }
  };

  const fetchIPRanges = async () => {
    try {
      const response = await connectoriprangesService.getAllConnectorIPRanges({ connectorid: id });
      setIpRanges(response.ipranges || []);
    } catch (err) {
      console.error('Error fetching IP ranges:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await connectorsettingsService.getAllConnectorSettings({ CONNECTORID: id });
      setSettings(response.settings || []);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchTrxSummaries = async () => {
    try {
      const response = await connectortrxsummaryService.getAllConnectorTrxSummaries({ connectorid: id });
      setTrxSummaries(response.summaries || []);
    } catch (err) {
      console.error('Error fetching transaction summaries:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If changing accountid, reset hotelid since hotels are filtered by account
    if (name === 'accountid') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        hotelid: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAccountSearch = (e) => {
    setAccountSearchTerm(e.target.value);
    setShowAccountDropdown(true);
  };

  const handleAccountSelect = (account) => {
    setFormData(prev => ({
      ...prev,
      accountid: account.id,
      hotelid: ''
    }));
    setAccountSearchTerm(`${account.id} - ${account.name}`);
    setShowAccountDropdown(false);
  };

  const filteredAccounts = accounts.filter(account => {
    const search = accountSearchTerm.toLowerCase();
    const id = account.id.toString().toLowerCase();
    const name = (account.name || '').toLowerCase();
    return id.includes(search) || name.includes(search);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.hotelid) {
      setError('Hotel ID is required');
      setLoading(false);
      return;
    }

    if (!formData.connectorName) {
      setError('Connector name is required');
      setLoading(false);
      return;
    }

    if (!formData.connectorType) {
      setError('Connector type is required');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting connector data:', formData);

      if (isCreateMode) {
        const response = await connectorService.createConnector(formData);
        console.log('Connector created:', response);
        // Navigate back to hotel view after create
        const targetHotelId = hotelid || formData.hotelid;
        if (targetHotelId) {
          navigate(`/hotel/${targetHotelId}`);
        } else {
          navigate('/partners');
        }
      } else if (isEditMode) {
        const response = await connectorService.updateConnector(id, formData);
        console.log('Connector updated:', response);
        // Navigate to connector view screen after update
        navigate(`/connectors/${id}`);
      }
    } catch (err) {
      console.error('Error saving connector:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save connector');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const targetHotelId = hotelid || formData.hotelid;
    if (isCreateMode && !hotelid) {
      // Creating connector from admin list, go back to admin connectors
      navigate('/admin/connectors');
    } else if (targetHotelId) {
      // Creating/editing from hotel page, go back to hotel
      navigate(`/hotel/${targetHotelId}`);
    } else {
      // Fallback
      navigate('/admin/connectors');
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="connector-form-container">Loading connector...</div>;
  }

  return (
    <div className="connector-form-container">
      <h2>
        {isCreateMode && 'Create New Connector'}
        {isEditMode && 'Edit Connector'}
        {isViewMode && 'View Connector'}
      </h2>

      {error && <div className="error-message">{error}</div>}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          type="button"
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'connection' ? 'active' : ''}`}
          onClick={() => setActiveTab('connection')}
        >
          Connection
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'configuration' ? 'active' : ''}`}
          onClick={() => setActiveTab('configuration')}
        >
          Configuration
        </button>
        {(isViewMode || isEditMode) && (
          <>
            <button
              type="button"
              className={`tab-button ${activeTab === 'ipranges' ? 'active' : ''}`}
              onClick={() => setActiveTab('ipranges')}
            >
              IP Ranges ({ipRanges.length})
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings ({settings.length})
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              Transactions ({trxSummaries.length})
            </button>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
            {/* Basic Information */}
            <div className="form-section">
          <h3>Basic Information</h3>

          {!isCreateMode && (
            <div className="form-group">
              <label htmlFor="id">Connector ID</label>
              <input
                type="text"
                id="id"
                name="id"
                value={formData.id || id}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="accountid">Chain ID *</label>
            {isCreateMode ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  id="accountid"
                  placeholder="Search for a chain..."
                  value={accountSearchTerm}
                  onChange={handleAccountSearch}
                  onFocus={() => setShowAccountDropdown(true)}
                  required={!formData.accountid}
                  autoComplete="off"
                />
                <input
                  type="hidden"
                  name="accountid"
                  value={formData.accountid}
                  required
                />
                {showAccountDropdown && filteredAccounts.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    zIndex: 1000,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {filteredAccounts.map(account => (
                      <div
                        key={account.id}
                        onClick={() => handleAccountSelect(account)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        {account.id} - {account.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <input
                type="text"
                id="accountid"
                name="accountid"
                value={formData.accountid}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="hotelid">Hotel ID *</label>
            {isCreateMode && !hotelid ? (
              <select
                id="hotelid"
                name="hotelid"
                value={formData.hotelid}
                onChange={handleChange}
                required
                disabled={!formData.accountid}
              >
                <option value="">
                  {!formData.accountid ? 'Select a chain first...' :
                   hotels.length === 0 ? 'No hotels found for this chain' :
                   'Select a hotel...'}
                </option>
                {hotels.map(hotel => (
                  <option key={hotel.hotelid} value={hotel.hotelid}>
                    {hotel.hotelid} - {hotel.hotelname}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="hotelid"
                name="hotelid"
                value={formData.hotelid}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Connector Details</h3>

          <div className="form-group">
            <label htmlFor="connectorName">Connector Name *</label>
            <input
              type="text"
              id="connectorName"
              name="connectorName"
              value={formData.connectorName}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="connectorType">Connector Type *</label>
            <input
              type="text"
              id="connectorType"
              name="connectorType"
              value={formData.connectorType}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="environment">Environment</label>
            <select
              id="environment"
              name="environment"
              value={formData.environment}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="connectorStatus">Status</label>
            <select
              id="connectorStatus"
              name="connectorStatus"
              value={formData.connectorStatus}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="version">Version</label>
            <input
              type="text"
              id="version"
              name="version"
              value={formData.version}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="region">Region</label>
            <input
              type="text"
              id="region"
              name="region"
              value={formData.region}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>
        </div>
          </>
        )}

        {/* Connection Tab */}
        {activeTab === 'connection' && (
          <>
            <div className="form-section">
              <h3>Connection Details</h3>

          <div className="form-group">
            <label htmlFor="connectorHost">Host</label>
            <input
              type="text"
              id="connectorHost"
              name="connectorHost"
              value={formData.connectorHost}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="connectorPort">Port</label>
            <input
              type="text"
              id="connectorPort"
              name="connectorPort"
              value={formData.connectorPort}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="connectorUsername">Username</label>
            <input
              type="text"
              id="connectorUsername"
              name="connectorUsername"
              value={formData.connectorUsername}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="connectorPassword">Password</label>
            <input
              type="password"
              id="connectorPassword"
              name="connectorPassword"
              value={formData.connectorPassword}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="connectorDBName">Database Name</label>
            <input
              type="text"
              id="connectorDBName"
              name="connectorDBName"
              value={formData.connectorDBName}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>SFTP Settings</h3>

          <div className="form-group">
            <label htmlFor="sftpfrequency">SFTP Frequency</label>
            <input
              type="text"
              id="sftpfrequency"
              name="sftpfrequency"
              value={formData.sftpfrequency}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="sftpfolder">SFTP Folder</label>
            <input
              type="text"
              id="sftpfolder"
              name="sftpfolder"
              value={formData.sftpfolder}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="sftpdeletefile">Delete File After Processing</label>
            <select
              id="sftpdeletefile"
              name="sftpdeletefile"
              value={formData.sftpdeletefile}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>
        </div>
          </>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <>
            {/* Additional Settings */}
            <div className="form-section">
              <h3>Additional Settings</h3>

          <div className="form-group">
            <label htmlFor="debug">Debug Mode</label>
            <select
              id="debug"
              name="debug"
              value={formData.debug}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="encryptpayload">Encrypt Payload</label>
            <select
              id="encryptpayload"
              name="encryptpayload"
              value={formData.encryptpayload}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="restrictip">Restrict IP</label>
            <select
              id="restrictip"
              name="restrictip"
              value={formData.restrictip}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="allowresyncmsg">Allow Resync Messages</label>
            <select
              id="allowresyncmsg"
              name="allowresyncmsg"
              value={formData.allowresyncmsg}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label htmlFor="connectorDescription">Description</label>
            <textarea
              id="connectorDescription"
              name="connectorDescription"
              value={formData.connectorDescription}
              onChange={handleChange}
              disabled={isViewMode}
              rows="4"
            />
          </div>
        </div>
          </>
        )}

        {/* Form Actions - Show on all tabs except ipranges and settings */}
        {(activeTab === 'general' || activeTab === 'connection' || activeTab === 'configuration') && (
          <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/connectors/${id}/edit`)}>
                Edit Connector
              </button>
              <button type="button" onClick={handleCancel}>
                Back to Hotel
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Connector' : 'Update Connector'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading}>
                Cancel
              </button>
            </>
          )}
          </div>
        )}
      </form>

      {/* IP Ranges Tab */}
      {activeTab === 'ipranges' && (isViewMode || isEditMode) && (
        <div className="connector-subsection">
          <div className="subsection-header">
            <h3>IP Ranges ({ipRanges.length})</h3>
            <button
              type="button"
              onClick={() => navigate(`/connector/${id}/iprange/new`)}
              className="btn-primary"
            >
              Add IP Range
            </button>
          </div>

          {ipRanges.length === 0 ? (
            <p className="no-items">No IP ranges configured for this connector.</p>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Allow From</th>
                    <th>Allow To</th>
                    <th>Account ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ipRanges
                    .slice((ipRangesCurrentPage - 1) * ipRangesPerPage, ipRangesCurrentPage * ipRangesPerPage)
                    .map((ipRange) => (
                      <tr
                        key={ipRange.connectoripid}
                        className="clickable-row"
                        onClick={() => navigate(`/connector/${id}/iprange/${ipRange.connectoripid}`)}
                        title="Click to view"
                      >
                        <td>{ipRange.connectoripid}</td>
                        <td>{ipRange.allowfrom}</td>
                        <td>{ipRange.allowto}</td>
                        <td>{ipRange.accountid || 'N/A'}</td>
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/connector/${id}/iprange/${ipRange.connectoripid}/edit`);
                            }}
                            className="btn-small"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Pagination for IP Ranges */}
              {ipRanges.length > ipRangesPerPage && (
                <div className="pagination">
                  <button
                    onClick={() => setIpRangesCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={ipRangesCurrentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {ipRangesCurrentPage} of {Math.ceil(ipRanges.length / ipRangesPerPage)}
                  </span>
                  <button
                    onClick={() => setIpRangesCurrentPage(prev => Math.min(prev + 1, Math.ceil(ipRanges.length / ipRangesPerPage)))}
                    disabled={ipRangesCurrentPage === Math.ceil(ipRanges.length / ipRangesPerPage)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (isViewMode || isEditMode) && (
        <div className="connector-subsection">
          <div className="subsection-header">
            <h3>Settings ({settings.length})</h3>
            <button
              type="button"
              onClick={() => navigate(`/connector/${id}/setting/new`)}
              className="btn-primary"
            >
              Add Setting
            </button>
          </div>

          {settings.length === 0 ? (
            <p className="no-items">No settings configured for this connector.</p>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Setting Name</th>
                    <th>Setting Value</th>
                    <th>Account ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settings
                    .map((setting) => (
                      <tr
                        key={setting.CONNECTORSETTINGSID}
                        className="clickable-row"
                        onClick={() => navigate(`/connector/${id}/setting/${setting.CONNECTORSETTINGSID}`)}
                        title="Click to view"
                      >
                        <td>{setting.CONNECTORSETTINGSID}</td>
                        <td>{setting.SETTINGNAME}</td>
                        <td className="setting-value">{setting.SETTINGVALUE ? (setting.SETTINGVALUE.length > 50 ? setting.SETTINGVALUE.substring(0, 50) + '...' : setting.SETTINGVALUE) : 'N/A'}</td>
                        <td>{setting.ACCOUNTID || 'N/A'}</td>
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/connector/${id}/setting/${setting.CONNECTORSETTINGSID}/edit`);
                            }}
                            className="btn-small"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (isViewMode || isEditMode) && (
        <div className="connector-subsection">
          <div className="subsection-header">
            <h3>Transaction Summaries ({trxSummaries.length})</h3>
            <button
              type="button"
              onClick={() => navigate(`/connector/${id}/trxsummary/new`)}
              className="btn-primary"
            >
              Add Summary
            </button>
          </div>

          {trxSummaries.length === 0 ? (
            <p className="no-items">No transaction summaries for this connector.</p>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Period</th>
                    <th>Transaction Type</th>
                    <th>Count</th>
                    <th>Last Transaction</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trxSummaries
                    .slice((trxSummariesCurrentPage - 1) * trxSummariesPerPage, trxSummariesCurrentPage * trxSummariesPerPage)
                    .map((summary) => (
                      <tr
                        key={summary.connectortrxsummaryid}
                        className="clickable-row"
                        onClick={() => navigate(`/connector/${id}/trxsummary/${summary.connectortrxsummaryid}`)}
                        title="Click to view"
                      >
                        <td>{summary.connectortrxsummaryid}</td>
                        <td>{summary.period || 'N/A'}</td>
                        <td>{summary.trxtype || 'N/A'}</td>
                        <td>{summary.trxcount || 0}</td>
                        <td>{summary.lasttrx ? new Date(summary.lasttrx).toLocaleString() : 'N/A'}</td>
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/connector/${id}/trxsummary/${summary.connectortrxsummaryid}/edit`);
                            }}
                            className="btn-small"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Pagination for Transaction Summaries */}
              {trxSummaries.length > trxSummariesPerPage && (
                <div className="pagination">
                  <button
                    onClick={() => setTrxSummariesCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={trxSummariesCurrentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {trxSummariesCurrentPage} of {Math.ceil(trxSummaries.length / trxSummariesPerPage)}
                  </span>
                  <button
                    onClick={() => setTrxSummariesCurrentPage(prev => Math.min(prev + 1, Math.ceil(trxSummaries.length / trxSummariesPerPage)))}
                    disabled={trxSummariesCurrentPage === Math.ceil(trxSummaries.length / trxSummariesPerPage)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ConnectorForm;
